import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  useWindowDimensions,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle, G } from 'react-native-svg';
import { formatPct } from '../../utils/formatPercentage';
import { organizationalUnitsApi, employeesApi, kpisApi, processesApi, unitGoalsApi } from '../../services/apiService';
import { invalidate as invalidateData } from '../../utils/dataInvalidation';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import DeletionApprovalBanner from '../../components/DeletionApprovalBanner';
import PendingDeletionBadge from '../../components/PendingDeletionBadge';
import { useNotification } from '../../contexts/NotificationContext';
import { exportEmployeesToExcel } from '../../utils/excelExport';
import { OrganizationalUnit, Employee, KPI, StrategicGoal, StrategicGoalList, Process, UnitGoal } from '../../types';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { DropdownPortal, useDropdownAnchor } from '../../components/DropdownPortal';
import { useRoleNavigation, useResponsive } from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { MainStackParamList } from '../../navigation/MainNavigator';

import { tokens } from '@design';
import { IconButton } from '@design/components/IconButton';
import { PageHeader } from '@design/components/PageHeader';
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;
type RouteProps = RouteProp<MainStackParamList, 'OrganizationalUnitDetail'>;

// Extended unit type with detail fields
interface UnitDetail extends OrganizationalUnit {
  children_count?: number;
  goals_count?: number;
  kpis_count?: number;
  processes_count?: number;
  employees_count?: number;
  achievement_percentage?: number;
}

// Icons
const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <Path
      d="M11.333 2L14 4.667M1.333 14.667L2 11.917L11.667 2.25L14 4.583L4.333 14.25L1.333 14.667Z"
      stroke={color}
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const SquarePlusIcon = ({ size = 14, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <Path d="M7 4.375V9.625M4.375 7H9.625" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M1.75 3.5C1.75 2.53 2.53 1.75 3.5 1.75H10.5C11.47 1.75 12.25 2.53 12.25 3.5V10.5C12.25 11.47 11.47 12.25 10.5 12.25H3.5C2.53 12.25 1.75 11.47 1.75 10.5V3.5Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EyeIcon = ({ size = 14, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Circle cx="12" cy="12" r="3" stroke={color} strokeWidth="2"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19 6V20C19 21.1 18.1 22 17 22H7C5.9 22 5 21.1 5 20V6M8 6V4C8 2.9 8.9 2 10 2H14C15.1 2 16 2.9 16 4V6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const DownloadIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M7 10L12 15L17 10" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15V3" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const UploadIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 8L12 3L7 8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 3V15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const UserPlusIcon = ({ size = 16, color = tokens.colors.raw.white }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8.5 11C10.7091 11 12.5 9.20914 12.5 7C12.5 4.79086 10.7091 3 8.5 3C6.29086 3 4.5 4.79086 4.5 7C4.5 9.20914 6.29086 11 8.5 11Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20 8V14" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M23 11H17" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Mini circular progress for performance column
const MiniCircularProgress = ({ percentage, size = 24 }: { percentage: number; size?: number }) => {
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (percentage / 100) * circumference;
  const color = percentage >= 80 ? tokens.colors.raw.green["500"] : percentage >= 50 ? tokens.colors.raw.yellow["500"] : tokens.colors.raw.red["500"];

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: tokens.spacing.absolute["1"] }}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#E5E5E5"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          rotation="-90"
          origin={`${size / 2}, ${size / 2}`}
        />
      </Svg>
      <Text style={{ fontSize: tokens.typography.paragraph["paragraph mini font size"], color: tokens.colors.semantic.general.foreground }}>{percentage} %</Text>
    </View>
  );
};

const SectionsIcon = () => (
  <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <Path d="M5 15L20 3.33L35 15V33.33C35 34.22 34.65 35.07 34.02 35.69C33.4 36.31 32.55 36.67 31.67 36.67H8.33C7.45 36.67 6.6 36.31 5.98 35.69C5.35 35.07 5 34.22 5 33.33V15Z" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const KPIsIcon = () => (
  <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <Path d="M5 5V35H35" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M30 11.67V28.33" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" strokeLinecap="round"/>
    <Path d="M20 16.67V28.33" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" strokeLinecap="round"/>
  </Svg>
);

const ProcessIcon = () => (
  <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <Path d="M26.67 3.33H10C8.23 3.33 6.67 4.9 6.67 6.67V33.33C6.67 35.1 8.23 36.67 10 36.67H30C31.77 36.67 33.33 35.1 33.33 33.33V10L26.67 3.33Z" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M26.67 3.33V10H33.33" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M15 18.33H25M15 25H21.67" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EmployeesIcon = () => (
  <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <Path d="M28.33 35V31.67C28.33 29.9 27.63 28.2 26.38 26.95C25.13 25.7 23.43 25 21.67 25H8.33C6.57 25 4.87 25.7 3.62 26.95C2.37 28.2 1.67 29.9 1.67 31.67V35" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M15 18.33C18.68 18.33 21.67 15.35 21.67 11.67C21.67 7.98 18.68 5 15 5C11.32 5 8.33 7.98 8.33 11.67C8.33 15.35 11.32 18.33 15 18.33Z" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M38.33 35V31.67C38.33 30.19 37.84 28.76 36.94 27.59C36.03 26.42 34.76 25.59 33.33 25.22" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M26.67 5.22C28.11 5.58 29.38 6.42 30.29 7.59C31.2 8.76 31.68 10.19 31.68 11.67C31.68 13.16 31.2 14.58 30.29 15.75C29.38 16.93 28.11 17.77 26.67 18.13" stroke={tokens.colors.semantic.general.foreground} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Donut Chart Component
const DonutChart = ({ data, size = 120 }: { data: { value: number; color: string; label: string }[]; size?: number }) => {
  const strokeWidth = 20;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  let accumulatedPercentage = 0;

  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Background circle */}
      <Circle
        cx={center}
        cy={center}
        r={radius}
        stroke="#E5E5E5"
        strokeWidth={strokeWidth}
        fill="transparent"
      />
      {/* Data segments */}
      <G rotation="-90" origin={`${center}, ${center}`}>
        {data.map((segment, index) => {
          const strokeDasharray = circumference;
          const strokeDashoffset = circumference - (segment.value / 100) * circumference;
          const rotation = (accumulatedPercentage / 100) * 360;
          accumulatedPercentage += segment.value;

          return (
            <Circle
              key={index}
              cx={center}
              cy={center}
              r={radius}
              stroke={segment.color}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              rotation={rotation}
              origin={`${center}, ${center}`}
              strokeLinecap="round"
            />
          );
        })}
      </G>
    </Svg>
  );
};

// Stat Card Component - matches Figma ArCard4
const StatCard = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) => {
  const { colors } = useTheme();
  return (
    <View style={[styles.statCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      <View style={styles.statCardHeader}>
        <Text style={[styles.statCardLabel, { color: colors.textPrimary }]}>{label}</Text>
        <View style={styles.statIconContainer}>{icon}</View>
      </View>
      <View style={styles.statCardValueContainer}>
        <Text style={[styles.statCardValue, { color: colors.textPrimary }]}>{value}</Text>
      </View>
    </View>
  );
};

// Goal Card Component
const GoalCard = ({ title, percentage, color }: { title: string; percentage: number; color: string }) => {
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  return (
    <View style={[styles.goalCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      <Text style={[styles.goalCardTitle, { color: colors.textPrimary }]}>{title}</Text>
      <View style={styles.goalProgressContainer}>
        <View style={[styles.goalProgressBar, { backgroundColor: isDark ? colors.gray200 : tokens.colors.raw.gray["200"] }]}>
          <View style={[styles.goalProgressFill, { width: `${percentage}%`, backgroundColor: color }]} />
        </View>
        <Text style={[styles.goalProgressText, { color }]}>%{percentage}</Text>
      </View>
      <TouchableOpacity style={styles.goalDetailsLink}>
        <ChevronLeftIcon size={14} color={colors.textMuted} />
        <Text style={[styles.goalDetailsText, { color: colors.textMuted }]}>{t('common.viewDetails')}</Text>
      </TouchableOpacity>
    </View>
  );
};

export default function OrganizationalUnitDetailScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const unitId = route.params?.id;
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { navigateToHome } = useRoleNavigation();
  const { width } = useWindowDimensions();
  const { horizontalCardWidth } = useResponsive();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { selectedYear } = useYear();

  // Theme colors object
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
  };

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [unit, setUnit] = useState<UnitDetail | null>(null);
  const [children, setChildren] = useState<OrganizationalUnit[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [kpis, setKpis] = useState<KPI[]>([]);
  const [goals, setGoals] = useState<StrategicGoalList[]>([]);
  const [unitGoals, setUnitGoals] = useState<UnitGoal[]>([]);
  const [processes, setProcesses] = useState<Process[]>([]);
  const [parentUnit, setParentUnit] = useState<OrganizationalUnit | null>(null);
  const [grandparentUnit, setGrandparentUnit] = useState<OrganizationalUnit | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Filter state for employees
  const [filterSection, setFilterSection] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showSectionDropdown, setShowSectionDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusAnchor = useDropdownAnchor(160);
  const typeAnchor = useDropdownAnchor(160);
  const sectionAnchor = useDropdownAnchor(180);

  // Delete employee modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Delete unit modal state - enhanced with DeletionImpactModal
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [deletionTargetId, setDeletionTargetId] = useState<number | null>(null);
  const [deletionTargetName, setDeletionTargetName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';
  const canApproveDeletion = ['org_executive', 'strategy_team', 'superuser'].includes(user?.profile?.user_type || '');

  // Determine hierarchy level based on tree depth (not unit_type):
  // Level 1 (إدارة/Directorate): top-level unit with no parent
  // Level 2 (قسم/Department): parent is a directorate (parent.parent === null)
  // Level 3 (شعبة/Section): parent is a department (parent.parent !== null)
  const isDirectorate = unit?.parent === null; // Level 1 - top-level unit
  const isDepartment = !isDirectorate && parentUnit !== null && parentUnit?.parent === null; // Level 2
  const isSection = !isDirectorate && parentUnit !== null && parentUnit?.parent !== null; // Level 3

  // Expandable sections
  const [sectionsExpanded, setSectionsExpanded] = useState(true);
  const [employeesExpanded, setEmployeesExpanded] = useState(true);
  const [kpisExpanded, setKpisExpanded] = useState(true);

  const isSmallScreen = width < 768;

  // Get unique sections from employees for filter dropdown
  const sectionOptions = React.useMemo(() => {
    const sections = new Set<string>();
    employees.forEach(emp => {
      if (emp.organizational_unit_name) {
        sections.add(emp.organizational_unit_name);
      }
    });
    return Array.from(sections);
  }, [employees]);

  // Filter employees based on selected filters
  const filteredEmployees = React.useMemo(() => {
    const filtered = employees.filter(emp => {
      // Filter by section
      if (filterSection !== 'all' && emp.organizational_unit_name !== filterSection) {
        return false;
      }
      // Filter by type (employee type - for now all are 'موظف')
      if (filterType !== 'all') {
        // Add type filtering logic when employee type field is available
      }
      // Filter by status (active/inactive)
      if (filterStatus !== 'all') {
        const isActive = (emp as any).is_active !== false; // Default to active
        if (filterStatus === 'active' && !isActive) return false;
        if (filterStatus === 'inactive' && isActive) return false;
      }
      return true;
    });
    return filtered;
  }, [employees, filterSection, filterType, filterStatus]);

  // Apply filter function
  const applyFilters = () => {
    // Close all dropdowns
    setShowSectionDropdown(false);
    setShowTypeDropdown(false);
    setShowStatusDropdown(false);
  };

  // Handle Excel export for employees
  const handleExportEmployees = () => {
    if (!filteredEmployees || filteredEmployees.length === 0) {
      alert(t('myOrganization.noEmployeesToExport'));
      return;
    }
    try {
      const exportData = filteredEmployees.map(emp => ({
        name: emp.name,
        name_ar: emp.name_ar || '',
        email: emp.email || '',
        phone: emp.phone || '',
        job_title: emp.job_title || '',
        organizational_unit_name: unit?.name || '',
        is_active: emp.is_active,
      }));
      exportEmployeesToExcel(exportData, [], `${t('myOrganization.employees')}_${unit?.name || t('myOrganization.unit')}`);
    } catch (error) {
      console.error('Export error:', error);
      alert(t('common.exportError'));
    }
  };

  // Reset filters
  const resetFilters = () => {
    setFilterSection('all');
    setFilterType('all');
    setFilterStatus('all');
  };

  // Employee action handlers
  const handleViewEmployee = (employee: Employee) => {
    // Navigate to EmployeeDetail screen for viewing
    navigation.navigate('EmployeeDetail', { id: employee.id });
  };

  const handleEditEmployee = (employee: Employee) => {
    navigation.navigate('AddEmployee', { employeeId: employee.id });
  };

  const handleEditCurrentUnit = () => {
    if (!unit) return;
    navigation.navigate('AddOrganizationalUnit', {
      unitId: unit.id,
      parentId: unit.parent ?? undefined,
      parentName: parentUnit?.name_ar || parentUnit?.name,
    });
  };

  const handleEditSection = (child: OrganizationalUnit) => {
    navigation.navigate('AddOrganizationalUnit', {
      unitId: child.id,
      parentId: unit?.id,
      parentName: unit?.name_ar || unit?.name,
    });
  };

  const handleDeleteSection = (child: OrganizationalUnit) => {
    setDeletionTargetId(child.id);
    setDeletionTargetName(child.name_ar || child.name);
    setShowDeletionImpact(true);
  };

  const handleDeleteCurrentUnit = () => {
    setDeletionTargetId(unitId);
    setDeletionTargetName(unit?.name_ar || unit?.name || '');
    setShowDeletionImpact(true);
  };

  const confirmDelete = async () => {
    if (!deletionTargetId) return;
    setIsDeleting(true);
    try {
      const response = await organizationalUnitsApi.delete(deletionTargetId);
      if ((response as any)?.status === 202) {
        showSuccess(t('deletion.requestSent'));
      } else {
        showSuccess(t('myOrganization.deleteSuccess'));
      }
      invalidateData('my-organization');
      if (deletionTargetId === unitId) {
        navigation.goBack();
      } else {
        setChildren(prev => prev.filter(c => c.id !== deletionTargetId));
      }
    } catch (error: any) {
      if (error.response?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        invalidateData('my-organization');
        if (deletionTargetId === unitId) {
          navigation.goBack();
        } else {
          fetchData();
        }
      } else if (error.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(t('myOrganization.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeletionImpact(false);
      setDeletionTargetId(null);
    }
  };

  const handleDeleteEmployee = (employee: Employee) => {
    setEmployeeToDelete(employee);
    setDeleteModalVisible(true);
  };

  const confirmDeleteEmployee = async () => {
    if (!employeeToDelete) return;

    setDeleting(true);
    try {
      await employeesApi.delete(employeeToDelete.id);
      // Refresh employees list from current unit AND all child units
      const allUnitIds = [unitId, ...children.map(child => child.id)];
      const employeePromises = allUnitIds.map((id: number) =>
        employeesApi.list({ organizational_unit: id, page_size: 100 })
      );
      const employeeResults = await Promise.all(employeePromises);
      const allEmployees = employeeResults.flatMap((result: any) => result.results || []);
      setEmployees(allEmployees);
      setDeleteModalVisible(false);
      setEmployeeToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete employee:', err);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteEmployee = () => {
    setDeleteModalVisible(false);
    setEmployeeToDelete(null);
  };

  const fetchData = useCallback(async () => {
    if (!unitId) return;

    try {
      setError(null);
      // First get the unit data
      const unitData = await organizationalUnitsApi.get(unitId);
      setUnit(unitData);

      // Fetch parent unit and grandparent for hierarchy breadcrumb
      if (unitData.parent) {
        try {
          const parentData = await organizationalUnitsApi.get(unitData.parent);
          setParentUnit(parentData);

          // Fetch grandparent if parent has a parent
          if (parentData.parent) {
            try {
              const grandparentData = await organizationalUnitsApi.get(parentData.parent);
              setGrandparentUnit(grandparentData);
            } catch (e) {
              setGrandparentUnit(null);
            }
          } else {
            setGrandparentUnit(null);
          }
        } catch (e) {
          setParentUnit(null);
          setGrandparentUnit(null);
        }
      } else {
        setParentUnit(null);
        setGrandparentUnit(null);
      }

      // Fetch children first to get their IDs for employee query
      const childrenData = await organizationalUnitsApi.list({ parent: unitId });
      const childrenUnits = childrenData.results || [];
      setChildren(childrenUnits);

      // Get all unit IDs (current unit + all children) for employee fetch
      const allUnitIds = [unitId, ...childrenUnits.map((child: any) => child.id)];

      // Fetch employees from current unit AND all child units
      const employeePromises = allUnitIds.map((id: number) =>
        employeesApi.list({ organizational_unit: id, page_size: 100 })
      );

      // Fetch other data in parallel with employee fetches
      const [kpisData, unitGoalsData, processesData, strategicGoalsData, ...employeeResults] = await Promise.all([
        kpisApi.list({ organizational_unit: unitId, page_size: 100 }),
        unitGoalsApi.list({ unit: unitId, page_size: 100 }),
        processesApi.list({ organizational_units: unitId, page_size: 100 }),
        organizationalUnitsApi.strategicGoals(unitId), // Get strategic goals this unit is involved in
        ...employeePromises,
      ]);

      // Combine all employees from all units
      const allEmployees = employeeResults.flatMap((result: any) => result.results || []);
      setEmployees(allEmployees);
      setKpis(kpisData.results || []);
      setUnitGoals(unitGoalsData.results || []);
      setProcesses(processesData.results || []);
      setGoals(strategicGoalsData || []);
    } catch (err: any) {
      setError(err.message || t('myOrganization.loadError'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [unitId]);

  // Re-fetch when unitId changes (navigating to same screen with different params)
  useEffect(() => {
    setLoading(true);
    setUnit(null);
    setParentUnit(null);
    setGrandparentUnit(null);
    setChildren([]);
    setEmployees([]);
    setKpis([]);
    setGoals([]);
    setUnitGoals([]);
    setProcesses([]);
    fetchData();
  }, [unitId]);

  // Refresh data when screen comes into focus (after adding a section)
  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Calculate KPI status distribution from actual KPI data
  const kpiStatusData = (() => {
    if (kpis.length === 0) {
      return [
        { value: 0, color: '#2ca02c', label: t('status.excellent') },
        { value: 0, color: '#ff7f0e', label: t('status.good') },
        { value: 0, color: '#d62728', label: t('status.needsImprovement') },
      ];
    }
    const excellent = kpis.filter(k => (k.achievement_percentage ?? 0) >= 80).length;
    const good = kpis.filter(k => (k.achievement_percentage ?? 0) >= 50 && (k.achievement_percentage ?? 0) < 80).length;
    const needsImprovement = kpis.filter(k => (k.achievement_percentage ?? 0) < 50).length;
    const total = kpis.length;
    return [
      { value: parseFloat(((excellent / total) * 100).toFixed(2)), color: '#2ca02c', label: t('status.excellent') },
      { value: parseFloat(((good / total) * 100).toFixed(2)), color: '#ff7f0e', label: t('status.good') },
      { value: parseFloat(((needsImprovement / total) * 100).toFixed(2)), color: '#d62728', label: t('status.needsImprovement') },
    ];
  })();

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={styles.loadingText}>{t('common.loading')}</Text>
        </View>
      </PageContainer>
    );
  }

  if (error || !unit) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error || t('myOrganization.unitNotFound')}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={fetchData}>
            <Text style={styles.retryButtonText}>{t('common.retry')}</Text>
          </TouchableOpacity>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollContent, isSmallScreen && { padding: tokens.spacing.absolute["4"] }]}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {/* Breadcrumb - based on hierarchy level */}
        <Breadcrumb
          items={
            isDepartment && parentUnit
              ? [
                  // Level 2: Home → My Org → Unit Details → Child Unit Details
                  { label: t('navigation.home') },
                  { label: t('navigation.myOrganization'), route: 'MyOrganization' },
                  { label: t('myOrganization.unitDetails'), route: 'OrganizationalUnitDetail', params: { id: parentUnit.id } },
                  { label: t('myOrganization.childUnitDetails') },
                ]
              : isSection && parentUnit && grandparentUnit
              ? [
                  // Level 3: Home → My Org → Unit Details → Child Unit Details → Child Unit Details
                  { label: t('navigation.home') },
                  { label: t('navigation.myOrganization'), route: 'MyOrganization' },
                  { label: t('myOrganization.unitDetails'), route: 'OrganizationalUnitDetail', params: { id: grandparentUnit.id } },
                  { label: t('myOrganization.childUnitDetails'), route: 'OrganizationalUnitDetail', params: { id: parentUnit.id } },
                  { label: t('myOrganization.childUnitDetails') },
                ]
              : [
                  // Level 1 or Root: Home → My Org → Unit Details
                  { label: t('navigation.home') },
                  { label: t('navigation.myOrganization'), route: 'MyOrganization' },
                  { label: t('myOrganization.unitDetails') },
                ]
          }
        />

        <PageHeader
          title={
            isSection && parentUnit
              ? `${parentUnit.name_ar || parentUnit.name} - ${unit.name_ar || unit.name}`
              : (unit.name_ar || unit.name)
          }
          description={unit.description || '—'}
          badge={(unit as any).has_pending_deletion ? <PendingDeletionBadge compact /> : undefined}
          actions={
            user?.profile?.user_type === 'org_employee' ? (
              <View style={styles.unitHeaderActions}>
                <IconButton
                  variant="secondary"
                  size="sm"
                  icon={<EditIcon color={tokens.colors.raw.slate["500"]} />}
                  accessibilityLabel={t('common.edit')}
                  onPress={handleEditCurrentUnit}
                />
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon={<TrashIcon color={tokens.colors.raw.red["500"]} />}
                  accessibilityLabel={t('common.delete')}
                  onPress={handleDeleteCurrentUnit}
                />
              </View>
            ) : undefined
          }
        />

        {/* Pending Deletion Banner */}
        {(unit as any).pending_deletion_info && (
          <DeletionApprovalBanner
            deletionInfo={(unit as any).pending_deletion_info}
            canApprove={canApproveDeletion}
            canCancel={isOrgEmployee && (unit as any).pending_deletion_info.requested_by_id === user?.id}
            onApproved={() => {
              showSuccess(t('deletion.approved'));
              invalidateData('my-organization');
              navigation.goBack();
            }}
            onRejected={() => {
              showSuccess(t('deletion.rejected'));
              fetchData();
            }}
            onCancelled={() => {
              showSuccess(t('deletion.requestCancelled'));
              fetchData();
            }}
          />
        )}

        {/* Unified layout: 4 Stats Row + Donut Chart & Children side by side */}
          <>
            {/* Stats Row - Single row of 4 cards */}
            <View style={[styles.statsRow, isSmallScreen && styles.statsRowSmall]}>
              <StatCard icon={<EmployeesIcon />} label={t('myOrganization.humanCapital')} value={unit.employees_count || employees.length} />
              <StatCard icon={<ProcessIcon />} label={t('unitDetail.mainProcesses')} value={unit.processes_count || processes.length} />
              <StatCard icon={<KPIsIcon />} label={t('kpis.title')} value={unit.kpis_count || kpis.length} />
              <StatCard icon={<SectionsIcon />} label={t('unitDetail.childUnits')} value={children.length} />
            </View>

            {/* Donut Chart & Sections Row - Side by side */}
            <View style={[styles.chartSectionsRow, isSmallScreen && styles.chartSectionsRowSmall]}>
              {/* Donut Chart Card - Left side */}
              <View style={[styles.chartCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isSmallScreen && styles.chartCardFullWidth]}>
                {/* Title at top */}
                <View style={styles.chartTitleContainer}>
                  <Text style={[styles.chartTitle, { color: themeColors.textPrimary }]}>{t('kpis.statusDistribution')}</Text>
                </View>
                {/* Chart and Legend row */}
                <View style={styles.chartContentRow}>
                  {/* Donut chart on left */}
                  <View style={styles.chartContainer}>
                    <DonutChart data={kpiStatusData} size={176} />
                  </View>
                  {/* Legend on right */}
                  <View style={styles.chartLegend}>
                    {kpiStatusData.map((item, index) => (
                      <View key={index} style={styles.legendItem}>
                        <Text style={[styles.legendValue, { color: themeColors.textPrimary }]}>{item.value} %</Text>
                        <View style={styles.legendLabelContainer}>
                          <Text style={[styles.legendLabel, { color: themeColors.textMuted }]}>{item.label}</Text>
                          <View style={[styles.legendBar, { backgroundColor: item.color }]} />
                        </View>
                      </View>
                    ))}
                  </View>
                </View>
              </View>

              {/* Sections Card - Right side */}
              <View style={[styles.sectionsCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isSmallScreen && styles.sectionsCardFullWidth]}>
                {/* Header with title and add button */}
                <View style={styles.sectionHeader}>
                  {isOrgEmployee && (
                    <TouchableOpacity
                      style={styles.addSectionButton}
                      onPress={() => navigation.navigate('AddOrganizationalUnit', {
                        parentId: unit.id,
                        parentName: unit.name_ar || unit.name,
                      })}
                    >
                      <Text style={styles.addSectionButtonText}>{t('unitDetail.addChildUnit')}</Text>
                      <SquarePlusIcon size={13} color={themeColors.iconDefault} />
                    </TouchableOpacity>
                  )}
                  <Text style={[styles.sectionTitle, { color: themeColors.textPrimary }]}>{t('unitDetail.childUnits')}</Text>
                </View>

                {/* Sections list */}
                <View style={styles.sectionsList}>
                  {children.length > 0 ? (
                    children.map((child) => (
                      <View key={child.id} style={styles.sectionItem}>
                        <View style={styles.sectionItemActions}>
                          <TouchableOpacity
                            style={styles.viewDetailsButton}
                            onPress={() => (navigation as any).push('OrganizationalUnitDetail', { id: child.id })}
                          >
                            <Text style={styles.viewDetailsButtonText}>{t('common.viewDetails')}</Text>
                            <EyeIcon size={13} color={tokens.colors.semantic.general.foreground} />
                          </TouchableOpacity>
                          {user?.profile?.user_type === 'org_employee' && (
                            <View style={styles.sectionEditDeleteRow}>
                              <TouchableOpacity onPress={() => handleDeleteSection(child)}>
                                <TrashIcon size={15} color={tokens.colors.raw.red["500"]} />
                              </TouchableOpacity>
                              <TouchableOpacity onPress={() => handleEditSection(child)}>
                                <EditIcon size={15} color={tokens.colors.raw.gray["500"]} />
                              </TouchableOpacity>
                            </View>
                          )}
                        </View>
                        <View style={styles.sectionItemPill}>
                          <View style={styles.sectionItemNameRow}>
                            <Text style={styles.sectionItemName}>{child.name_ar || child.name}</Text>
                            {(child as any).has_pending_deletion && (
                              <PendingDeletionBadge compact />
                            )}
                          </View>
                          <Text style={styles.sectionItemCount}>{child.kpis_count || 0} {t('kpis.title')}</Text>
                        </View>
                      </View>
                    ))
                  ) : (
                    <View style={styles.emptyGoalsContainer}>
                      <Text style={styles.emptyGoalsText}>{t('unitDetail.noChildUnits')}</Text>
                    </View>
                  )}
                </View>
              </View>
            </View>
          </>

        {/* KPI Performance Indicators Section */}
        <View style={styles.unitGoalsSection}>
          <View style={[styles.goalsSectionCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <View style={styles.goalsSectionHeader}>
              {isOrgEmployee && (
                <TouchableOpacity
                  style={styles.addGoalButtonOutlined}
                  onPress={() => navigation.navigate('AddOrganizationalUnit', {
                    unitId: unit?.id,
                    parentId: unit?.parent ?? undefined,
                    parentName: parentUnit?.name_ar || parentUnit?.name,
                  })}
                >
                  <Text style={styles.addGoalButtonOutlinedText}>{t('kpis.addIndicator')}</Text>
                  <SquarePlusIcon size={13} color={tokens.colors.semantic.general.foreground} />
                </TouchableOpacity>
              )}
              <Text style={[styles.goalsSectionTitle, { color: themeColors.textPrimary }]}>{t('kpis.performanceIndicators')}</Text>
            </View>

            <View style={styles.goalsContainer}>
              {kpis.length > 0 ? (
                <View style={styles.rtlWrapper}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    style={styles.horizontalScrollContainer}
                    contentContainerStyle={styles.horizontalCardsRow}
                  >
                    {kpis.map((kpi) => (
                      <View key={kpi.id} style={[styles.rtlCard, styles.kpiIndicatorCard, { width: horizontalCardWidth, backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
                        <View style={styles.kpiCardNameContainer}>
                          <Text style={[styles.kpiCardName, { color: themeColors.textSecondary }]} numberOfLines={2}>
                            {kpi.name}
                          </Text>
                        </View>
                        <View style={styles.kpiCardStatsRow}>
                          <View style={[styles.kpiStatBox, { backgroundColor: themeColors.backgroundAlt }]}>
                            <Text style={[styles.kpiStatLabel, { color: themeColors.textSecondary }]}>{t('common.achieved')}</Text>
                            <View style={styles.kpiStatValueRow}>
                              <Text style={[styles.kpiStatUnit, { color: kpi.latest_value != null ? '#2ca02c' : themeColors.textSecondary }]}>
                                {kpi.unit === 'percentage' ? '%' : kpi.unit === 'number' ? 'عدد' : kpi.unit}
                              </Text>
                              <Text style={[styles.kpiStatValue, { color: kpi.latest_value != null ? '#2ca02c' : themeColors.textSecondary }]}>
                                {kpi.latest_value != null ? kpi.latest_value : '-'}
                              </Text>
                            </View>
                          </View>
                          <View style={[styles.kpiStatBox, { backgroundColor: themeColors.backgroundAlt }]}>
                            <Text style={[styles.kpiStatLabel, { color: themeColors.textSecondary }]}>{t('common.target')}</Text>
                            <View style={styles.kpiStatValueRow}>
                              <Text style={[styles.kpiStatUnit, { color: themeColors.textSecondary }]}>
                                {kpi.unit === 'percentage' ? '%' : kpi.unit === 'number' ? 'عدد' : kpi.unit}
                              </Text>
                              <Text style={[styles.kpiStatValue, { color: themeColors.textSecondary }]}>
                                {kpi.target_value || '-'}
                              </Text>
                            </View>
                          </View>
                        </View>
                        <TouchableOpacity
                          style={styles.kpiViewDetailsButton}
                          onPress={() => navigation.navigate('KPIDetail', { id: kpi.id })}
                        >
                          <ChevronLeftIcon size={16} color={tokens.colors.semantic.general.foreground} />
                          <Text style={[styles.kpiViewDetailsText, { color: themeColors.textSecondary }]}>{t('common.viewDetails')}</Text>
                        </TouchableOpacity>
                      </View>
                    ))}
                  </ScrollView>
                </View>
              ) : (
                <View style={styles.emptyGoalsContainer}>
                  <Text style={styles.emptyGoalsText}>{t('kpis.noKPIs')}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Strategic Goals Section - matches Figma exactly */}
        <View style={styles.goalsSection}>
          {/* Header Card */}
          <View style={[styles.goalsSectionCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <View style={styles.goalsSectionHeader}>
              {isOrgEmployee && (
                <TouchableOpacity
                  style={styles.addGoalButtonOutlined}
                  onPress={() => navigation.navigate('AddGoal', { organizationId: unit?.department_id, unitId: unit?.id })}
                >
                  <Text style={styles.addGoalButtonOutlinedText}>{t('unitDetail.addStrategicGoal')}</Text>
                  <SquarePlusIcon size={13} color={tokens.colors.semantic.general.foreground} />
                </TouchableOpacity>
              )}
              <Text style={[styles.goalsSectionTitle, { color: themeColors.textPrimary }]}>{t('goals.strategicGoals')}</Text>
            </View>

            {/* Goals Container with gray background - Horizontal Scroll with RTL transform */}
            <View style={styles.goalsContainer}>
              {goals.length > 0 ? (
                <View style={styles.rtlWrapper}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    style={styles.horizontalScrollContainer}
                    contentContainerStyle={styles.horizontalCardsRow}
                  >
                    {goals.map((goal) => {
                      const percentage = goal.progress_percentage || goal.achievement_percentage || 0;
                      const progressColor = percentage >= 75 ? '#2ca02c' : '#ff7f0e';
                      return (
                        <View key={goal.id} style={[styles.rtlCard, styles.strategicGoalCard, { width: horizontalCardWidth }]}>
                          <View style={styles.strategicGoalContent}>
                            <Text style={styles.strategicGoalTitle}>{goal.title}</Text>
                            <View style={styles.strategicGoalProgress}>
                              <View style={styles.strategicGoalPercentageBadge}>
                                <Text style={styles.strategicGoalPercentageText}>% {percentage}</Text>
                              </View>
                              <View style={styles.strategicGoalProgressBar}>
                                <View style={[styles.strategicGoalProgressFill, { width: `${percentage}%`, backgroundColor: progressColor }]} />
                                <View style={[styles.strategicGoalProgressIndicator, { backgroundColor: progressColor, start: `${100 - percentage}%` }]} />
                              </View>
                            </View>
                          </View>
                          <TouchableOpacity
                            style={styles.strategicGoalDetailsLink}
                            onPress={() => navigation.navigate('GoalDetail', { id: goal.id })}
                          >
                            <ChevronLeftIcon size={16} color={tokens.colors.semantic.general.foreground} />
                            <Text style={styles.strategicGoalDetailsText}>{t('common.viewDetails')}</Text>
                          </TouchableOpacity>
                        </View>
                      );
                    })}
                  </ScrollView>
                </View>
              ) : (
                <View style={styles.emptyGoalsContainer}>
                  <Text style={styles.emptyGoalsText}>{t('unitDetail.noStrategicGoals')}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Unit Goals Section - matches Figma exactly */}
        <View style={styles.unitGoalsSection}>
          <View style={[styles.goalsSectionCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <View style={styles.goalsSectionHeader}>
              {isOrgEmployee && (
                <TouchableOpacity
                  style={styles.addGoalButtonOutlined}
                  onPress={() => navigation.navigate('AddUnitGoal', { unitId: unit.id })}
                >
                  <Text style={styles.addGoalButtonOutlinedText}>{t('unitDetail.addGoal')}</Text>
                  <SquarePlusIcon size={13} color={tokens.colors.semantic.general.foreground} />
                </TouchableOpacity>
              )}
              <Text style={[styles.goalsSectionTitle, { color: themeColors.textPrimary }]}>{t('unitGoals.title')}</Text>
            </View>

            {/* Goals Container with gray background - Horizontal Scroll with RTL transform */}
            <View style={styles.goalsContainer}>
              {unitGoals.length > 0 ? (
                <View style={styles.rtlWrapper}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    style={styles.horizontalScrollContainer}
                    contentContainerStyle={styles.horizontalCardsRow}
                  >
                    {unitGoals.map((goal) => {
                      const percentage = goal.achievement_percentage || 0;
                      const progressColor = percentage >= 75 ? '#2ca02c' : '#ff7f0e';
                      return (
                        <View key={goal.id} style={[styles.rtlCard, styles.strategicGoalCard, { width: horizontalCardWidth }]}>
                          <View style={styles.strategicGoalContent}>
                            <Text style={styles.strategicGoalTitle}>{goal.name}</Text>
                            <View style={styles.strategicGoalProgress}>
                              <View style={styles.strategicGoalPercentageBadge}>
                                <Text style={styles.strategicGoalPercentageText}>% {percentage}</Text>
                              </View>
                              <View style={styles.strategicGoalProgressBar}>
                                <View style={[styles.strategicGoalProgressFill, { width: `${percentage}%`, backgroundColor: progressColor }]} />
                                <View style={[styles.strategicGoalProgressIndicator, { backgroundColor: progressColor, start: `${100 - percentage}%` }]} />
                              </View>
                            </View>
                          </View>
                          <TouchableOpacity
                            style={styles.strategicGoalDetailsLink}
                            onPress={() => navigation.navigate('UnitGoalDetail', { id: goal.id })}
                          >
                            <ChevronLeftIcon size={16} color={tokens.colors.semantic.general.foreground} />
                            <Text style={styles.strategicGoalDetailsText}>{t('common.viewDetails')}</Text>
                          </TouchableOpacity>
                        </View>
                      );
                    })}
                  </ScrollView>
                </View>
              ) : (
                <View style={styles.emptyGoalsContainer}>
                  <Text style={styles.emptyGoalsText}>{t('unitDetail.noUnitGoals')}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Main Processes Section - matches Figma exactly */}
        <View style={styles.unitGoalsSection}>
          <View style={[styles.goalsSectionCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <View style={styles.goalsSectionHeader}>
              {isOrgEmployee && (
                <TouchableOpacity
                  style={styles.addGoalButtonOutlined}
                  onPress={() => navigation.navigate('AddProcess', { organizationalUnitId: unit?.id })}
                >
                  <Text style={styles.addGoalButtonOutlinedText}>{t('unitDetail.addProcess')}</Text>
                  <SquarePlusIcon size={13} color={tokens.colors.semantic.general.foreground} />
                </TouchableOpacity>
              )}
              <Text style={[styles.goalsSectionTitle, { color: themeColors.textPrimary }]}>{t('unitDetail.mainProcesses')}</Text>
            </View>

            <View style={styles.goalsContainer}>
              {processes.length > 0 ? (
                <View style={styles.rtlWrapper}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    style={styles.horizontalScrollContainer}
                    contentContainerStyle={styles.horizontalCardsRow}
                  >
                    {processes.map((process) => {
                      const percentage = process.achievement_percentage || 0;
                      const progressColor = percentage >= 75 ? '#2ca02c' : percentage >= 50 ? '#ff7f0e' : '#d62728';
                      return (
                        <View key={process.id} style={[styles.rtlCard, styles.strategicGoalCard, { width: horizontalCardWidth }]}>
                          <View style={styles.strategicGoalContent}>
                            <Text style={styles.strategicGoalTitle}>{process.name}</Text>
                            <View style={styles.strategicGoalProgress}>
                              <View style={styles.strategicGoalPercentageBadge}>
                                <Text style={styles.strategicGoalPercentageText}>% {percentage}</Text>
                              </View>
                              <View style={styles.strategicGoalProgressBar}>
                                <View style={[styles.strategicGoalProgressFill, { width: `${percentage}%`, backgroundColor: progressColor }]} />
                                <View style={[styles.strategicGoalProgressIndicator, { backgroundColor: progressColor, start: `${100 - percentage}%` }]} />
                              </View>
                            </View>
                          </View>
                          <TouchableOpacity
                            style={styles.strategicGoalDetailsLink}
                            onPress={() => navigation.navigate('ProcessDetail', { id: process.id })}
                          >
                            <ChevronLeftIcon size={16} color={tokens.colors.semantic.general.foreground} />
                            <Text style={styles.strategicGoalDetailsText}>{t('common.viewDetails')}</Text>
                          </TouchableOpacity>
                        </View>
                      );
                    })}
                  </ScrollView>
                </View>
              ) : (
                <View style={styles.emptyGoalsContainer}>
                  <Text style={styles.emptyGoalsText}>{t('unitDetail.noProcesses')}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Employees Section - Matches Figma exactly */}
        <View style={styles.employeesSection}>
          {/* Header with title and action buttons - Matches Figma exactly */}
          {/* In LTR row with justify-between: first=LEFT, last=RIGHT */}
          <View style={styles.employeesSectionHeader}>
            {/* Buttons FIRST - appears on LEFT */}
            <View style={styles.employeesActions}>
              {/* Order: إضافة موظف (leftmost) | استيراد قائمة | تصدير Excel (rightmost) */}
              {isOrgEmployee && (
                <TouchableOpacity
                  style={styles.employeesActionBtn}
                  onPress={() => navigation.navigate('AddEmployee', { unitId: unit?.id })}
                >
                  <UserPlusIcon size={14} color={tokens.colors.semantic.general.foreground} />
                  <Text style={styles.employeesActionBtnText}>{t('employees.addEmployee')}</Text>
                </TouchableOpacity>
              )}
              {isOrgEmployee && (
                <TouchableOpacity style={styles.employeesActionBtn}>
                  <UploadIcon size={14} color={tokens.colors.semantic.general.foreground} />
                  <Text style={styles.employeesActionBtnText}>{t('employees.importList')}</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={styles.employeesActionBtn} onPress={handleExportEmployees}>
                <DownloadIcon size={14} color={tokens.colors.semantic.general.foreground} />
                <Text style={styles.employeesActionBtnText}>{t('common.exportExcel')}</Text>
              </TouchableOpacity>
            </View>
            {/* Title LAST - appears on RIGHT */}
            <Text style={styles.employeesSectionTitle}>{t('myOrganization.employees')}</Text>
          </View>

          {/* Filter Row - Matches Figma exactly: 3 dropdowns + button */}
          <View style={styles.employeesFilterRow}>
            {/* تطبيق الفلتر button - LEFT side */}
            <TouchableOpacity style={[styles.applyFilterBtnGray, { backgroundColor: themeColors.backgroundAlt }]} onPress={applyFilters}>
              <Text style={[styles.applyFilterBtnGrayText, { color: themeColors.textPrimary }]}>{t('common.applyFilter')}</Text>
            </TouchableOpacity>

            {/* جميع الحالات dropdown */}
            <View style={styles.filterDropdownFlex}>
              <View ref={statusAnchor.ref as any} collapsable={false}>
                <TouchableOpacity
                  style={[styles.filterDropdownTouchable, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
                  onPress={() => {
                    if (!showStatusDropdown) statusAnchor.measure();
                    setShowStatusDropdown(!showStatusDropdown);
                    setShowTypeDropdown(false);
                    setShowSectionDropdown(false);
                  }}
                >
                  <ChevronDownIcon size={14} color={themeColors.textPrimary} />
                  <Text style={[styles.filterDropdownTextDark, { color: themeColors.textPrimary }]}>
                    {filterStatus === 'all' ? t('filters.allStatuses') : filterStatus === 'active' ? t('filters.active') : t('filters.inactive')}
                  </Text>
                </TouchableOpacity>
              </View>
              <DropdownPortal
                open={showStatusDropdown}
                onClose={() => setShowStatusDropdown(false)}
                pos={statusAnchor.pos}
                backgroundColor={themeColors.card}
                borderColor={themeColors.cardBorder}
              >
                <TouchableOpacity style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterStatus('all'); setShowStatusDropdown(false); }}>
                  <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterStatus === 'all' && styles.dropdownItemTextActive]}>{t('filters.allStatuses')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterStatus('active'); setShowStatusDropdown(false); }}>
                  <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterStatus === 'active' && styles.dropdownItemTextActive]}>{t('filters.active')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterStatus('inactive'); setShowStatusDropdown(false); }}>
                  <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterStatus === 'inactive' && styles.dropdownItemTextActive]}>{t('filters.inactive')}</Text>
                </TouchableOpacity>
              </DropdownPortal>
            </View>

            {/* جميع الأنواع dropdown */}
            <View style={styles.filterDropdownFlex}>
              <View ref={typeAnchor.ref as any} collapsable={false}>
                <TouchableOpacity
                  style={[styles.filterDropdownTouchable, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
                  onPress={() => {
                    if (!showTypeDropdown) typeAnchor.measure();
                    setShowTypeDropdown(!showTypeDropdown);
                    setShowStatusDropdown(false);
                    setShowSectionDropdown(false);
                  }}
                >
                  <ChevronDownIcon size={14} color={themeColors.textPrimary} />
                  <Text style={[styles.filterDropdownTextDark, { color: themeColors.textPrimary }]}>
                    {filterType === 'all' ? t('filters.allTypes') : filterType === 'employee' ? t('filters.employee') : t('filters.manager')}
                  </Text>
                </TouchableOpacity>
              </View>
              <DropdownPortal
                open={showTypeDropdown}
                onClose={() => setShowTypeDropdown(false)}
                pos={typeAnchor.pos}
                backgroundColor={themeColors.card}
                borderColor={themeColors.cardBorder}
              >
                <TouchableOpacity style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterType('all'); setShowTypeDropdown(false); }}>
                  <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterType === 'all' && styles.dropdownItemTextActive]}>{t('filters.allTypes')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterType('employee'); setShowTypeDropdown(false); }}>
                  <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterType === 'employee' && styles.dropdownItemTextActive]}>{t('filters.employee')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterType('manager'); setShowTypeDropdown(false); }}>
                  <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterType === 'manager' && styles.dropdownItemTextActive]}>{t('filters.manager')}</Text>
                </TouchableOpacity>
              </DropdownPortal>
            </View>

            {/* جميع الأقسام dropdown - RIGHT side */}
            <View style={styles.filterDropdownFlex}>
              <View ref={sectionAnchor.ref as any} collapsable={false}>
                <TouchableOpacity
                  style={[styles.filterDropdownTouchable, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
                  onPress={() => {
                    if (!showSectionDropdown) sectionAnchor.measure();
                    setShowSectionDropdown(!showSectionDropdown);
                    setShowStatusDropdown(false);
                    setShowTypeDropdown(false);
                  }}
                >
                  <ChevronDownIcon size={14} color={themeColors.textPrimary} />
                  <Text style={[styles.filterDropdownTextDark, { color: themeColors.textPrimary }]}>
                    {filterSection === 'all' ? t('filters.allSections') : filterSection}
                  </Text>
                </TouchableOpacity>
              </View>
              <DropdownPortal
                open={showSectionDropdown}
                onClose={() => setShowSectionDropdown(false)}
                pos={sectionAnchor.pos}
                backgroundColor={themeColors.card}
                borderColor={themeColors.cardBorder}
                maxHeight={300}
              >
                <ScrollView style={{ maxHeight: 280 }} nestedScrollEnabled>
                  <TouchableOpacity style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterSection('all'); setShowSectionDropdown(false); }}>
                    <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterSection === 'all' && styles.dropdownItemTextActive]}>{t('filters.allSections')}</Text>
                  </TouchableOpacity>
                  {sectionOptions.map((section, index) => (
                    <TouchableOpacity key={index} style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterSection(section); setShowSectionDropdown(false); }}>
                      <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterSection === section && styles.dropdownItemTextActive]} numberOfLines={1}>{section}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </DropdownPortal>
            </View>
          </View>

          {/* Employees Table - Matches Figma exactly */}
          <ScrollView horizontal={isSmallScreen} showsHorizontalScrollIndicator={isSmallScreen}>
          <View style={[styles.employeesTableContainer, isSmallScreen && { minWidth: 700 }]}>
            {/* Table Header */}
            <View style={styles.employeesTableHeader}>
              {/* Action icons column - no header */}
              <View style={[styles.employeesHeaderCell, { width: 72 }]} />
              {/* الحالة */}
              <Text style={[styles.employeesHeaderCell, { width: 60 }]}>{t('table.status')}</Text>
              {/* الأداء */}
              <Text style={[styles.employeesHeaderCell, { width: 80 }]}>{t('table.performance')}</Text>
              {/* النوع */}
              <Text style={[styles.employeesHeaderCell, { width: 70 }]}>{t('table.type')}</Text>
              {/* الهاتف */}
              <Text style={[styles.employeesHeaderCell, { flex: 1 }]}>{t('table.phone')}</Text>
              {/* البريد الالكتروني */}
              <Text style={[styles.employeesHeaderCell, { flex: 1.3 }]}>{t('table.email')}</Text>
              {/* القسم with sort */}
              <View style={[styles.employeesHeaderCell, { flex: 1.2, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: tokens.spacing.absolute["1"] }]}>
                <ChevronDownIcon size={10} color={tokens.colors.raw.slate["500"]} />
                <Text style={styles.employeesHeaderCellText}>{t('table.section')}</Text>
              </View>
              {/* رقم الموظف with sort */}
              <View style={[styles.employeesHeaderCell, { width: 100, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: tokens.spacing.absolute["1"] }]}>
                <ChevronDownIcon size={10} color={tokens.colors.raw.slate["500"]} />
                <Text style={styles.employeesHeaderCellText}>{t('table.employeeNumber')}</Text>
              </View>
              {/* اسم الحساب with sort */}
              <View style={[styles.employeesHeaderCell, { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: tokens.spacing.absolute["1"] }]}>
                <ChevronDownIcon size={10} color={tokens.colors.raw.slate["500"]} />
                <Text style={styles.employeesHeaderCellText}>{t('table.accountName')}</Text>
              </View>
            </View>

            {/* Table Rows */}
            {filteredEmployees.length > 0 ? (
              filteredEmployees.slice(0, 8).map((emp, index) => (
                <View key={emp.id || index} style={styles.employeesTableRow}>
                  {/* Action Icons - trash, edit, view */}
                  <View style={[styles.employeesCell, { width: 72, flexDirection: 'row', gap: tokens.spacing.absolute["3"], justifyContent: 'flex-start' }]}>
                    <TouchableOpacity onPress={() => handleDeleteEmployee(emp)}><TrashIcon size={16} color={tokens.colors.raw.gray["400"]} /></TouchableOpacity>
                    <TouchableOpacity onPress={() => handleEditEmployee(emp)}><EditIcon size={16} color={tokens.colors.raw.gray["400"]} /></TouchableOpacity>
                    <TouchableOpacity onPress={() => handleViewEmployee(emp)}><EyeIcon size={16} color={tokens.colors.raw.gray["400"]} /></TouchableOpacity>
                  </View>
                  {/* Status - نشط */}
                  <View style={[styles.employeesCell, { width: 60 }]}>
                    <View style={styles.statusBadgeGreen}>
                      <Text style={styles.statusBadgeGreenText}>{t('filters.active')}</Text>
                    </View>
                  </View>
                  {/* Performance with circular progress */}
                  <View style={[styles.employeesCell, { width: 80, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: tokens.spacing.absolute["1_5"] }]}>
                    <Text style={styles.performanceText}>94 %</Text>
                    <View style={styles.performanceCircle}>
                      <Svg width={24} height={24} viewBox="0 0 24 24">
                        <Circle cx={12} cy={12} r={10} stroke="#E5E5E5" strokeWidth={3} fill="transparent" />
                        <Circle
                          cx={12} cy={12} r={10}
                          stroke={tokens.colors.raw.green["500"]} strokeWidth={3} fill="transparent"
                          strokeDasharray={62.83}
                          strokeDashoffset={62.83 * (1 - 0.94)}
                          strokeLinecap="round"
                          rotation="-90" origin="12, 12"
                        />
                      </Svg>
                    </View>
                  </View>
                  {/* Type - موظف (GREEN badge) */}
                  <View style={[styles.employeesCell, { width: 70 }]}>
                    <View style={styles.typeBadgeGreen}>
                      <Text style={styles.typeBadgeGreenText}>{t('filters.employee')}</Text>
                    </View>
                  </View>
                  {/* Phone */}
                  <Text style={[styles.employeesCell, { flex: 1 }]}>{emp.phone || '-'}</Text>
                  {/* Email */}
                  <Text style={[styles.employeesCell, { flex: 1.3 }]}>{emp.email || '-'}</Text>
                  {/* Section */}
                  <Text style={[styles.employeesCell, { flex: 1.2 }]} numberOfLines={2}>{emp.organizational_unit_name || '-'}</Text>
                  {/* Employee Number */}
                  <Text style={[styles.employeesCell, { width: 100 }]}>{emp.employee_number || '-'}</Text>
                  {/* Account Name */}
                  <Text style={[styles.employeesCell, { flex: 1 }]}>{emp.name_ar || emp.name_en || '-'}</Text>
                </View>
              ))
            ) : (
              <View style={styles.emptyTableRow}>
                <Text style={styles.emptyText}>{t('employees.noEmployees')}</Text>
              </View>
            )}
          </View>
          </ScrollView>

          {/* Pagination - Matches Figma exactly */}
          <View style={[styles.employeesPagination, { flexDirection: rtlRow }]}>
            <View style={[styles.paginationButtons, { flexDirection: rtlRow }]}>
              <TouchableOpacity style={styles.paginationBtn}>
                <Text style={styles.paginationBtnText}>{t('common.previous')}</Text>
                <Svg width={14} height={14} viewBox="0 0 24 24" fill="none">
                  <Path d="M9 18L15 12L9 6" stroke={tokens.colors.raw.slate["500"]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              </TouchableOpacity>
              <View style={styles.paginationPageNumber}>
                <Text style={styles.paginationPageText}>1</Text>
              </View>
              <TouchableOpacity style={styles.paginationBtn}>
                <ChevronLeftIcon size={14} color={tokens.colors.raw.slate["500"]} />
                <Text style={styles.paginationBtnText}>{t('common.next')}</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.paginationInfo}>{t('pagination.showing', { start: 1, end: Math.min(8, filteredEmployees.length), total: filteredEmployees.length })}</Text>
          </View>
        </View>

      </ScrollView>

      {/* Delete Employee Confirmation Modal */}
      <Modal
        visible={deleteModalVisible}
        transparent
        animationType="fade"
        onRequestClose={cancelDeleteEmployee}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('common.confirmDelete')}</Text>
            <Text style={styles.modalMessage}>
              {t('employees.confirmDeleteEmployee')} "{employeeToDelete?.name_ar || employeeToDelete?.name_en || t('common.thisEmployee')}"?
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.modalCancelButton}
                onPress={cancelDeleteEmployee}
              >
                <Text style={styles.modalCancelButtonText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalDeleteButton, deleting && styles.modalButtonDisabled]}
                onPress={confirmDeleteEmployee}
                disabled={deleting}
              >
                {deleting ? (
                  <ActivityIndicator size="small" color={tokens.colors.raw.white} />
                ) : (
                  <Text style={styles.modalDeleteButtonText}>{t('common.delete')}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => { setShowDeletionImpact(false); setDeletionTargetId(null); }}
        onConfirm={confirmDelete}
        entityName={deletionTargetName}
        fetchImpact={async () => {
          if (!deletionTargetId) return { total_affected: 0, can_restore: true, items: [], unlinked_kpis: [] };
          const response = await organizationalUnitsApi.deletionImpact(deletionTargetId);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.absolute["6"],
    paddingBottom: tokens.spacing.absolute["10"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.neutral600,
    fontFamily: FONTS.regular,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.absolute["6"],
  },
  errorText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.raw.red["500"],
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["4"],
    fontFamily: FONTS.regular,
  },
  retryButton: {
    backgroundColor: COLORS.primary,
    paddingHorizontal: tokens.spacing.absolute["6"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  retryButtonText: {
    color: tokens.colors.raw.white,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
  },
  breadcrumbText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#737373',
    fontFamily: FONTS.regular,
  },
  breadcrumbSeparator: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#D4D4D4',
    fontFamily: FONTS.regular,
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.primary,
    fontFamily: FONTS.medium,
  },
  unitHeaderActions: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["2"],
  },
  // Stats Row - Single row of 4 cards
  statsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  statsRowSmall: {
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["3"],
  },
  // Chart and Stats row (Figma layout)
  chartStatsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  chartStatsRowSmall: {
    flexDirection: 'column',
  },
  statsGrid: {
    flex: 1,
    flexDirection: 'column',
    gap: tokens.spacing.absolute["5"],
  },
  statsGridRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["5"],
  },
  statCard: {
    flex: 1,
    minWidth: 120,
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  statCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  statIconContainer: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statCardLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    flex: 1,
    fontFamily: FONTS.regular,
  },
  statCardValueContainer: {
    marginTop: tokens.spacing.absolute["4"],
    alignItems: 'center',
  },
  statCardValue: {
    fontSize: 30,
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
    fontFamily: FONTS.numbers,
    fontWeight: '500',
  },
  // Chart & Sections Row - Side by side
  chartSectionsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  chartSectionsRowSmall: {
    flexDirection: 'column',
    gap: tokens.spacing.absolute["4"],
  },
  chartCard: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["5"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    flexDirection: 'column',
    gap: tokens.spacing.absolute["5"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  chartCardFullWidth: {
    flex: 1,
    width: '100%',
  },
  chartTitleContainer: {
    alignItems: 'flex-end',
  },
  chartTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.medium,
  },
  chartContentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  chartContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  chartLegend: {
    alignItems: 'flex-end',
    gap: tokens.spacing.absolute["6"],
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  legendLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1"],
    width: 100,
  },
  legendBar: {
    width: 8,
    height: 32,
    borderRadius: tokens.radii.absolute["radius-2"],
  },
  legendLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.slate["500"],
    textAlign: 'center',
    fontFamily: FONTS.regular,
  },
  legendValue: {
    fontSize: tokens.typography["heading 4"]["font size"],
    color: '#303030',
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.numbers,
    fontWeight: '500',
  },
  sectionsCard: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    gap: tokens.spacing.absolute["4"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  sectionsCardFullWidth: {
    width: '100%',
  },
  sectionsFullWidthCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.absolute["6"],
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    flex: 1,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.medium,
  },
  sectionsList: {
    flex: 1,
    gap: tokens.spacing.absolute["2"],
  },
  sectionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  sectionItemActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
  },
  sectionEditDeleteRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  sectionItemPill: {
    flex: 1,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    alignItems: 'flex-end',
  },
  sectionItemNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    justifyContent: 'flex-end',
  },
  sectionItemName: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
    fontFamily: FONTS.regular,
  },
  sectionItemCount: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.slate["500"],
    textAlign: getTextAlign(isRTL),
    lineHeight: 14,
    fontFamily: FONTS.regular,
  },
  addSectionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["1_5"],
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 5.5,
    minHeight: 32,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  addSectionButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.medium,
  },
  viewDetailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["1_5"],
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 5.5,
    minHeight: 32,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  viewDetailsButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.regular,
  },
  emptyText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#737373',
    textAlign: 'center',
    paddingVertical: tokens.spacing.absolute["3"],
    fontFamily: FONTS.regular,
  },
  // Goals Section
  goalsSection: {
    marginBottom: tokens.spacing.absolute["4"],
  },
  goalsSectionCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    gap: tokens.spacing.absolute["4"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  goalsSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  goalsSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    flex: 1,
    fontFamily: FONTS.medium,
  },
  addGoalButtonOutlined: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["1_5"],
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 5.5,
    minHeight: 32,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  addGoalButtonOutlinedText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.medium,
  },
  goalsContainer: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
  },
  // Horizontal scroll styles for goal cards
  horizontalScrollContainer: {
    flexGrow: 0,
  },
  // RTL wrapper - flips the entire ScrollView horizontally so cards start from right
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  // RTL card - flips each card back to normal orientation
  rtlCard: {
    transform: [{ scaleX: -1 }],
  },
  horizontalCardsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["1"],
    paddingBottom: tokens.spacing.absolute["3"],
  },
  // Strategic Goal Card - matches Figma (now with responsive width)
  strategicGoalCard: {
    flexShrink: 0, // Prevent shrinking in horizontal scroll
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    gap: tokens.spacing.absolute["4"],
  },
  strategicGoalContent: {
    flex: 1,
    gap: tokens.spacing.absolute["4"],
  },
  strategicGoalTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
    fontFamily: FONTS.regular,
  },
  strategicGoalProgress: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  strategicGoalPercentageBadge: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: 100,
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
  },
  strategicGoalPercentageText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
    fontFamily: FONTS.numbers,
    fontWeight: '500',
  },
  strategicGoalProgressBar: {
    flex: 1,
    height: 8,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    overflow: 'visible',
    position: 'relative',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  strategicGoalProgressFill: {
    height: 8,
    borderRadius: tokens.radii.absolute["radius-8"],
    position: 'absolute',
    end: 0,
    top: 0,
  },
  strategicGoalProgressIndicator: {
    width: 14,
    height: 14,
    borderRadius: tokens.radii.absolute["radius-7"],
    borderWidth: 2,
    borderColor: '#FCFCFC',
    position: 'absolute',
    top: -3,
    transform: [{ translateX: -7 }],
  },
  strategicGoalDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
    minHeight: 24,
  },
  strategicGoalDetailsText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  // Old goal card styles (keep for backwards compatibility)
  goalCard: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    gap: tokens.spacing.absolute["3"],
  },
  goalCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
    fontFamily: FONTS.regular,
  },
  goalProgressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  goalProgressBar: {
    flex: 1,
    height: 8,
    backgroundColor: '#E5E5E5',
    borderRadius: tokens.radii.absolute["radius-4"],
    overflow: 'hidden',
  },
  goalProgressFill: {
    height: '100%',
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  goalProgressText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
  },
  goalDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["1"],
  },
  goalDetailsText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: '#737373',
    fontFamily: FONTS.regular,
  },
  // Unit Goals Section
  unitGoalsSection: {
    marginBottom: tokens.spacing.absolute["4"],
  },
  unitGoalCard: {
    backgroundColor: tokens.colors.raw.slate["50"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
  },
  unitGoalTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["3"],
    fontFamily: FONTS.regular,
  },
  unitGoalProgress: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  unitGoalProgressBar: {
    flex: 1,
    height: 8,
    backgroundColor: '#E5E5E5',
    borderRadius: tokens.radii.absolute["radius-4"],
    overflow: 'hidden',
  },
  unitGoalProgressFill: {
    height: '100%',
    backgroundColor: tokens.colors.raw.green["500"],
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  unitGoalProgressText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.green["500"],
    fontFamily: FONTS.medium,
  },
  // Table Section
  tableSection: {
    marginBottom: tokens.spacing.absolute["4"],
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["4"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  tableSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacing.absolute["3"],
  },
  tableSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.medium,
  },
  tableActions: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["2"],
  },
  tableActionButton: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
  },
  tableActionText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.regular,
  },
  addEmployeeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.green["500"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: tokens.radii.absolute["radius-8"],
    gap: tokens.spacing.absolute["1"],
  },
  addEmployeeButtonText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.white,
    fontFamily: FONTS.medium,
  },
  filterButton: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: COLORS.primary,
  },
  filterButtonText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.primary,
    fontFamily: FONTS.regular,
  },
  tableContainer: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: tokens.colors.raw.slate["50"],
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
  },
  tableHeaderCell: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.slate["500"],
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.medium,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["100"],
    alignItems: 'center',
  },
  tableCell: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  tableCellText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.regular,
  },
  emptyTableRow: {
    paddingVertical: tokens.spacing.absolute["6"],
    alignItems: 'center',
  },
  statusBadge: {
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  statusBadgeText: {
    fontSize: 10,
    color: tokens.colors.raw.white,
    fontFamily: FONTS.medium,
  },
  typeBadge: {
    backgroundColor: '#E0F2FE',
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  typeBadgeText: {
    fontSize: 10,
    color: '#0369A1',
    fontFamily: FONTS.medium,
  },
  tableFooter: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: '#737373',
    textAlign: 'center',
    marginTop: tokens.spacing.absolute["3"],
    fontFamily: FONTS.regular,
  },
  // Empty states
  emptyGoalsContainer: {
    paddingVertical: tokens.spacing.absolute["6"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyGoalsText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#737373',
    textAlign: 'center',
    fontFamily: FONTS.regular,
  },
  // Process percentage badge
  processPercentageBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
  },
  processPercentageText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
  },
  // ============================================================
  // Employees Section Styles - Matches Figma
  // ============================================================
  employeesSection: {
    marginBottom: tokens.spacing.absolute["4"],
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["4"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  employeesSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacing.absolute["4"],
  },
  employeesSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.medium,
  },
  employeesActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  employeesActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    backgroundColor: tokens.colors.raw.white,
  },
  employeesActionBtnText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.regular,
  },
  addEmployeeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.green["500"],
  },
  addEmployeeBtnText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.white,
    fontFamily: FONTS.medium,
  },
  employeesFilterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  applyFilterBtn: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    backgroundColor: tokens.colors.raw.white,
  },
  applyFilterBtnText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.regular,
  },
  filterDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    backgroundColor: tokens.colors.raw.white,
  },
  filterDropdownText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.slate["500"],
    fontFamily: FONTS.regular,
  },
  // New filter styles matching Figma exactly
  applyFilterBtnGray: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 5.5,
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.slate["100"],
    minHeight: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  applyFilterBtnGrayText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#0F172A',
    fontFamily: FONTS.regular,
  },
  filterDropdownFlex: {
    flex: 1,
    position: 'relative',
    zIndex: 10,
  },
  filterDropdownTouchable: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1_5"],
    paddingStart: tokens.spacing.absolute["0_5"],
    paddingEnd: tokens.spacing.absolute["2"],
    paddingVertical: 5.5,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    backgroundColor: tokens.colors.raw.white,
    minHeight: 32,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  filterDropdownTextDark: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  dropdownMenu: {
    position: 'absolute',
    top: 36,
    start: 0,
    end: 0,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 5,
    zIndex: 100,
    maxHeight: 200,
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["100"],
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  dropdownItemTextActive: {
    color: tokens.colors.semantic.general["Main Color"],
    fontFamily: FONTS.medium,
  },
  employeesTableContainer: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    overflow: 'hidden',
  },
  employeesTableHeader: {
    flexDirection: 'row',
    backgroundColor: tokens.colors.raw.slate["50"],
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
  },
  employeesHeaderCell: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.slate["500"],
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.medium,
  },
  employeesHeaderCellText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.slate["500"],
    fontFamily: FONTS.medium,
  },
  employeesTableRow: {
    flexDirection: 'row',
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["100"],
    alignItems: 'center',
  },
  employeesCell: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  statusBadgeGreen: {
    backgroundColor: tokens.colors.raw.green["500"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  statusBadgeGreenText: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    color: tokens.colors.raw.white,
    fontFamily: FONTS.medium,
  },
  typeBadgeBlue: {
    backgroundColor: '#DBEAFE',
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  typeBadgeBlueText: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    color: '#1D4ED8',
    fontFamily: FONTS.medium,
  },
  typeBadgeGreen: {
    backgroundColor: tokens.colors.raw.green["500"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  typeBadgeGreenText: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    color: tokens.colors.raw.white,
    fontFamily: FONTS.medium,
  },
  performanceText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.regular,
  },
  performanceCircle: {
    width: 24,
    height: 24,
  },
  employeesPagination: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["1"],
  },
  paginationButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  paginationBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: tokens.radii.absolute["radius-4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    backgroundColor: tokens.colors.raw.white,
  },
  paginationBtnText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.slate["500"],
    fontFamily: FONTS.regular,
  },
  paginationPageNumber: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: tokens.radii.absolute["radius-4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    backgroundColor: tokens.colors.raw.white,
  },
  paginationPageText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.regular,
  },
  paginationInfo: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.slate["500"],
    fontFamily: FONTS.regular,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    width: '90%',
    maxWidth: 400,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)',
    elevation: 10,
  },
  modalTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["3"],
    fontFamily: FONTS.medium,
  },
  modalMessage: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.slate["500"],
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["6"],
    lineHeight: 22,
    fontFamily: FONTS.regular,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["6"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    backgroundColor: tokens.colors.raw.white,
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    fontFamily: FONTS.medium,
  },
  modalDeleteButton: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["6"],
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.red["500"],
    alignItems: 'center',
  },
  modalDeleteButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.white,
    fontFamily: FONTS.medium,
  },
  modalButtonDisabled: {
    opacity: 0.6,
  },
  // ============================================================
  // KPIs Section Styles - Matches Figma exactly
  // ============================================================
  kpisSection: {
    marginBottom: tokens.spacing.absolute["4"],
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["5"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    gap: tokens.spacing.absolute["5"],
  },
  kpisTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  kpisSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 24,
    fontFamily: FONTS.medium,
  },
  kpisFilterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["4"],
  },
  kpisApplyFilterBtn: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 5.5,
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.slate["100"],
    minHeight: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpisApplyFilterBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#0F172A',
    fontFamily: FONTS.regular,
  },
  kpisFilterDropdownFlex: {
    flex: 1,
    position: 'relative',
  },
  kpisFilterDropdownTouchable: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1_5"],
    paddingStart: tokens.spacing.absolute["0_5"],
    paddingEnd: tokens.spacing.absolute["2"],
    paddingVertical: 5.5,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    backgroundColor: tokens.colors.raw.white,
    minHeight: 32,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  kpisFilterDropdownText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  kpisTableContainer: {
    backgroundColor: tokens.colors.raw.white,
    overflow: 'hidden',
  },
  kpisTableHeader: {
    flexDirection: 'row',
    paddingVertical: 7.5,
    paddingHorizontal: tokens.spacing.absolute["2"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
  },
  kpisHeaderCell: {
    paddingHorizontal: tokens.spacing.absolute["2"],
  },
  kpisHeaderCellText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontWeight: '700',
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    paddingHorizontal: tokens.spacing.absolute["2"],
    lineHeight: 19.6,
  },
  kpisTableRow: {
    flexDirection: 'row',
    paddingVertical: 7.5,
    paddingHorizontal: tokens.spacing.absolute["2"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
    alignItems: 'center',
  },
  kpisCell: {
    paddingHorizontal: tokens.spacing.absolute["2"],
    flexDirection: 'row',
    alignItems: 'center',
  },
  kpisCellText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    paddingHorizontal: tokens.spacing.absolute["2"],
    lineHeight: 13.57,
  },
  kpisCheckbox: {
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiStatusBadge: {
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    minHeight: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiStatusBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.slate["50"],
    textAlign: 'center',
    lineHeight: 13.57,
  },
  kpisPagination: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  kpisPaginationButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  kpisPaginationBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: 7.5,
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: 'transparent',
    minHeight: 36,
    opacity: 0.5,
  },
  kpisPaginationBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontWeight: '700',
    color: tokens.colors.raw.slate["700"],
  },
  kpisPaginationPageNumber: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: 7.5,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    backgroundColor: tokens.colors.raw.white,
    minHeight: 36,
    minWidth: 34,
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  kpisPaginationPageText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontWeight: '700',
    color: tokens.colors.semantic.general.foreground,
  },
  kpisPaginationInfo: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.slate["500"],
    lineHeight: 19.6,
  },
  // KPI Performance Indicators Cards
  kpiIndicatorCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    gap: tokens.spacing.absolute["2"],
  },
  kpiCardNameContainer: {
    flex: 1,
    alignItems: 'flex-end',
  },
  kpiCardName: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
    lineHeight: 20,
  },
  kpiCardStatsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["2"],
  },
  kpiStatBox: {
    flex: 1,
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    gap: tokens.spacing.absolute["1"],
    alignItems: 'flex-end',
  },
  kpiStatLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
    width: '100%',
  },
  kpiStatValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  kpiStatUnit: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
  },
  kpiStatValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '600',
    textAlign: getTextAlign(isRTL),
  },
  kpiViewDetailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingVertical: 3,
    paddingHorizontal: tokens.spacing.absolute["2"],
    minHeight: 24,
    borderRadius: tokens.radii.absolute["radius-4"],
    alignSelf: 'flex-start',
  },
  kpiViewDetailsText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
});

const styles = getStyles(true);
